import React, {
  FC,
  useRef,
  useMemo,
  useState,
  useEffect,
} from 'react';
import {
  Dialog,
  InputText,
  Button,
  Messages,
  RadioButton,
  Dropdown,
} from '@agro1desenvolvimento/react-components';
import classNames from 'classnames';
import * as Yup from 'yup';
import { ptForm } from 'yup-locale-pt';
import { useFormik } from 'formik';
import { useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { selectTopBarState } from '../../../components/Topbar/topBarSlice';
import { Deposito } from '../../../@types/estoque/deposito';
import { createOrUpdateDeposito, fetchDepositos } from './depositosSlice';
import findOrThrow from '../../../utilities/find-or-trow';
import catchApiErrorsAndSetFormErrors from '../../../utilities/catch-api-errors';
import ShowErrorHelper from '../../../components/ShowErrorHelper';
import { CrudCreateUpdate, transitionOptionsTimeout as timeout } from '../../../utilities';
import { fetchPessoas, selectPessoaState } from '../../geral/Pessoas/pessoaSlice';

const Footer: React.FC<{close: () => void, valid: boolean}> = ({ close, valid }) => (
  <div>
    <Button label="Cancelar" icon="pi pi-times" onClick={close} className="p-button-text" />
    <Button label="Salvar" disabled={!valid} icon="pi pi-check" autoFocus type="submit" form="form-save-deposito" />
  </div>
);

Yup.setLocale(ptForm);
const DepositoSchema = Yup.object().shape({
  descricao: Yup.string().required(),
  proprietario: Yup.string().required(),
  tipo: Yup.string().required(),
});

interface DepositoFormField {
  id?: string,
  descricao?: string,
  proprietario?: string,
  tipo?: string,
}

interface FormularioProps {
  deposito?: Deposito
}

const FormDeposito: FC<FormularioProps> = ({ deposito }) => {
  const { currentProdutor } = useSelector(selectTopBarState);
  const messages = useRef<Messages>(null);
  const history = useHistory();
  const { pessoas } = useSelector(selectPessoaState);
  const dispatch = useDispatch();
  const [modalVisible, setModalVisible] = useState(true);
  const tiposDepositos = [{ key: 'proprio', name: 'Próprio' }, { key: 'terceiro', name: 'Terceiro' }];

  const close = () => {
    history.push('/estoque/depositos');
  };

  const fechaModal = () => {
    setModalVisible(false);
  };

  const form = useFormik<DepositoFormField>({
    validationSchema: DepositoSchema,
    initialValues: {
      id: deposito?.id,
      descricao: deposito?.descricao,
      proprietario: deposito?.proprietario?.id || currentProdutor?.id,
      tipo: deposito?.tipo || 'proprio',
    },

    onSubmit: async (values) => {
      const toSubmit: Partial<CrudCreateUpdate<Deposito>> = {
        id: values.id,
        descricao: values.descricao,
        tipo: values.tipo as 'proprio' | 'terceiro' | undefined,
        proprietario: findOrThrow(pessoas, { id: values.proprietario }),
      };

      const promise = await dispatch(createOrUpdateDeposito(toSubmit));
      const success = promise.meta.requestStatus === 'fulfilled';
      if (success) {
        fechaModal();
        dispatch(fetchDepositos());
      } else {
        catchApiErrorsAndSetFormErrors(form.setFieldError, promise.payload, true);
      }
    },
  });

  useEffect(() => {
    dispatch(fetchPessoas());
  }, []);

  const classes = useMemo(() => (
    classNames({ 'p-invalid': form.errors.descricao })
  ), [form.errors]);

  const handleTipoChange = (e: { target: { value: any; }; }) => {
    const tipoSelecionado = e.target.value;
    form.setFieldValue('tipo', tipoSelecionado);

    if (tipoSelecionado === 'proprio') {
      form.setFieldValue('proprietario', currentProdutor?.id);
    }
    form.handleChange(e);
  };

  return (
    <Dialog
      onHide={fechaModal}
      transitionOptions={{ timeout, onExited: close }}
      visible={modalVisible}
      maximizable
      header={deposito?.id ? 'Alterar depósito' : 'Adicionar depósito'}
      className="dialog-md"
      footer={<Footer close={fechaModal} valid={form.dirty && form.isValid} />}
    >
      <form onSubmit={form.handleSubmit} id="form-save-deposito">
        <div className="p-fluid">
          <div className="p-field">
            {
              tiposDepositos.map((tipo) => (
                <div key={tipo.key} className="p-field-radiobutton">
                  <RadioButton
                    inputId={tipo.key}
                    name="tipo"
                    value={tipo.key}
                    key={tipo.key}
                    onChange={handleTipoChange}
                    checked={form.values.tipo === tipo.key}
                  />
                  <label htmlFor={tipo.key}>{tipo.name}</label>
                </div>
              ))
            }
            <ShowErrorHelper id="tipo-help" error={form.errors.tipo} />
          </div>
          <div className="p-field">
            <label htmlFor="descricao">Descrição</label>
            <InputText
              autoFocus
              required
              name="descricao"
              id="descricao"
              value={form.values.descricao}
              onChange={form.handleChange}
              className={classes}
              aria-describedby="descricao-help"
            />
            <ShowErrorHelper id="descricao-help" error={form.errors.descricao} />
          </div>
          <div className="p-field">
            <label aria-labelledby="proprietario" htmlFor="proprietario">Proprietário</label>
            <Dropdown
              autoFocus
              value={form.values.proprietario}
              name="proprietario"
              id="proprietario"
              options={pessoas}
              optionLabel="nome"
              optionValue="id"
              disabled={!pessoas?.length || form.values.tipo === 'proprio'}
              onChange={form.handleChange}
              required
              placeholder="Selecione o Proprietário"
              appendTo={document.body}
              filter
              className={classNames({ 'p-invalid': form.errors.proprietario })}
              aria-describedby="proprietario-help"
            />
            <ShowErrorHelper id="proprietario-help" error={form.errors.proprietario} />
          </div>
        </div>
      </form>
      <Messages ref={messages} />
    </Dialog>
  );
};

export default FormDeposito;
